import { Link as RouterLink } from "react-router";
import { CopyButton } from "./CopyButton";
import { Card, CardHeader, Skeleton } from "./ui";
import { formatCount } from "../lib/money";
import type { Link } from "../lib/types";
import { useTranslation } from "react-i18next";

/**
 * The affiliate's link, on the screen they land on.
 *
 * It was not here, and that was the largest thing wrong with this dashboard.
 * An affiliate's whole job is *sharing a link*, and the home screen showed four
 * balances, three counts and a chart — everything about the link's results and
 * nothing they could actually paste into anything. Copying it meant a trip to
 * another page, every single time, for the one action the product exists to
 * cause.
 *
 * So it is first, above the money. The numbers below it are what happened to
 * this link, which is the right way round: the link is the thing, and the
 * balance is its consequence.
 *
 * ---------------------------------------------------------------------------
 * One link and not the list
 * ---------------------------------------------------------------------------
 * The oldest live one, which is the default `syncAffiliate` creates and the one
 * most affiliates only ever use. Somebody running several campaigns is
 * precisely the person who goes to Links, and the row of counts under this one
 * carries a link there.
 *
 * Showing all of them here would rebuild the Links page on the home screen, and
 * the home screen would then have two tables and no answer to "what do I
 * paste".
 */
export function PrimaryLink({
  links,
  pending,
  failed,
}: {
  links: Link[] | undefined;
  pending: boolean;
  /* A failed request is not an empty list. Without this the card states "you
     have no live links" — a fact about the account — on the strength of a
     request that never answered, and tells somebody their links are gone when
     the network hiccuped. */
  failed?: boolean;
}) {
  const { t } = useTranslation("overview");
  if (failed) return null;

  if (pending) {
    return (
      <Card>
        <CardHeader title={t("yourLink.title")} />
        <div className="px-4 py-4">
          <Skeleton className="h-9 w-full" />
        </div>
      </Card>
    );
  }

  const live = (links ?? []).filter((link) => !link.archived);
  const primary = live[0];

  /*
   * No link at all is a state a real account can reach — every affiliate is
   * given one on first sign-in, so this means they archived it. Rare, and it
   * has to say something better than an empty box, because an affiliate with no
   * live link earns nothing and there is no other signal that this is why.
   */
  if (!primary) {
    return (
      <Card>
        <CardHeader
          title={t("yourLink.title")}
          description={t("yourLink.none")}
        />
        <div className="px-4 py-4">
          <RouterLink
            className="text-[13px] text-accent transition-colors duration-200 ease-smooth hover:underline"
            to="/links"
          >
            Create one →
          </RouterLink>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        title={t("yourLink.title")}
        description={t("yourLink.share")}
        action={
          live.length > 1 ? (
            <RouterLink
              className="text-[13px] text-fg-muted transition-colors duration-200 ease-smooth hover:text-accent"
              to="/links"
            >
              {t("yourLink.all", { count: live.length })}
            </RouterLink>
          ) : undefined
        }
      />
      <div className="space-y-3 px-4 py-4">
        {/*
         * The URL and the button on one row, and the URL is the field-looking
         * thing rather than a line of prose: `bg-canvas` inside a `surface`
         * card is the same recess `Input` uses, which is what makes it read as
         * something to take rather than something to read.
         *
         * `select-all` so a tap selects the whole URL. Somebody on a phone who
         * misses the Copy button and drags at the text gets the entire link
         * rather than a word of it — and half a referral link is a lost
         * commission that looks like a working one.
         */}
        <div className="flex flex-wrap items-center gap-2">
          <code className="min-w-0 flex-1 rounded-md border border-line bg-canvas px-3 py-2 font-mono text-[13px] break-all text-fg select-all">
            {primary.url}
          </code>
          <CopyButton value={primary.url} />
        </div>

        {/* What this link has done, so the card is not only a control. Linked,
            because the next question after "how many clicks" is "from where",
            and that is the campaign screen. */}
        <RouterLink
          className="inline-flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-fg-subtle transition-colors duration-200 ease-smooth hover:text-accent"
          to={`/links/${primary.id}`}
        >
          <span>{t("yourLink.clicks", { count: formatCount(primary.clicks) })}</span>
          <span>{t("yourLink.signups", { count: formatCount(primary.referrals) })}</span>
          <span>{t("yourLink.customers", { count: formatCount(primary.conversions) })}</span>
        </RouterLink>
      </div>
    </Card>
  );
}
